(() => {
  const root = document.querySelector('[data-wired808-popup]');
  if (!root || root.dataset.wired808Ready === 'true') return;
  root.dataset.wired808Ready = 'true';

  const teaser = document.querySelector('[data-wired808-teaser]');
  const teaserOpen = teaser?.querySelector('[data-wired808-teaser-open]');
  const teaserClose = teaser?.querySelector('[data-wired808-teaser-close]');
  const veil = root.querySelector('[data-wired808-veil]');
  const shell = root.querySelector('.wired808-discount-popup__shell');
  const back = root.querySelector('.wired808-discount-popup__back');
  const email = root.querySelector('input[type="email"]');
  const form = root.querySelector('form');
  const submit = root.querySelector('button[type="submit"]');
  const copyButton = root.querySelector('[data-wired808-copy-code]');
  const closeButton = root.querySelector('.wired808-discount-popup__close');
  const success = Boolean(root.querySelector('[data-wired808-success]'));
  const hasErrors = Boolean(root.querySelector('[data-wired808-error]'));
  const force = new URLSearchParams(window.location.search).get('wired808_popup') === '1';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sessionKey = 'wired808-discount-popup-seen-v1';
  const suppressionKey = 'wired808-discount-popup-suppressed-until-v1';
  const teaserSuppressionKey = 'wired808-discount-teaser-suppressed-until-v1';
  const day = 24 * 60 * 60 * 1000;
  const dismissalDays = 1;
  const normalDelay = 900;
  const consentProbeDelay = 1500;
  const landingDelay = 2450;
  let landingTimer = 0;
  let consentTimer = 0;
  let closeTimer = 0;
  let lastFocus = null;
  let previousBodyOverflow = '';
  let previousDocumentOverflow = '';
  let isOpen = false;

  const storageFor = (name) => {
    try {
      return window[name];
    } catch (_error) {
      return null;
    }
  };

  const safeGet = (storageName, key) => {
    try {
      return storageFor(storageName)?.getItem(key) || null;
    } catch (_error) {
      return null;
    }
  };

  const safeSet = (storageName, key, value) => {
    try {
      storageFor(storageName)?.setItem(key, value);
    } catch (_error) {
      // Storage can be unavailable in privacy modes; the popup still remains usable.
    }
  };

  const suppressFor = (days) => {
    const candidateUntil = Date.now() + days * day;
    const currentUntil = Number.parseInt(safeGet('localStorage', suppressionKey) || '0', 10);
    const nextUntil = Number.isFinite(currentUntil)
      ? Math.max(currentUntil, candidateUntil)
      : candidateUntil;
    safeSet('localStorage', suppressionKey, String(nextUntil));
  };

  const isSuppressed = () => {
    const value = Number.parseInt(safeGet('localStorage', suppressionKey) || '0', 10);
    return Number.isFinite(value) && value > Date.now();
  };

  const suppressTeaserFor = (days) => {
    safeSet('localStorage', teaserSuppressionKey, String(Date.now() + days * day));
  };

  const teaserIsSuppressed = () => {
    const value = Number.parseInt(safeGet('localStorage', teaserSuppressionKey) || '0', 10);
    return Number.isFinite(value) && value > Date.now();
  };

  const hideTeaser = () => {
    if (!teaser) return;
    teaser.hidden = true;
  };

  const showTeaser = () => {
    if (!teaser || isOpen || teaserIsSuppressed()) return;
    syncPreviewBarInset();
    teaser.hidden = false;
    teaserOpen?.setAttribute('aria-expanded', 'false');
  };

  const isVisible = (element) => {
    if (!element || element.hidden) return false;
    const style = window.getComputedStyle(element);
    const bounds = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && bounds.width > 0 && bounds.height > 0;
  };

  const consentIsVisible = () => isVisible(document.querySelector('#shopify-pc__banner'));

  const syncPreviewBarInset = () => {
    const previewBar = document.querySelector('#PBarNextFrameWrapper');
    const height = isVisible(previewBar) ? Math.ceil(previewBar.getBoundingClientRect().height) : 0;
    root.style.setProperty('--wired808-preview-bar-height', `${height}px`);
    teaser?.style.setProperty('--wired808-preview-bar-height', `${height}px`);
    root.classList.toggle('has-preview-bar', height > 0);
  };

  const focusable = () => Array.from(root.querySelectorAll(
    'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter((element) => element.tabIndex >= 0 && !element.closest('[inert]') && element.getClientRects().length > 0);

  const focusOffer = () => {
    const target = success ? copyButton : email;
    root.classList.add('is-initial-focus');
    if (target) target.focus({ preventScroll: true });
    else shell.focus({ preventScroll: true });
  };

  const land = () => {
    root.classList.remove('is-animating');
    root.classList.add('is-landed');
    back.removeAttribute('inert');
    focusOffer();
  };

  const restoreScroll = () => {
    document.body.style.overflow = previousBodyOverflow;
    document.documentElement.style.overflow = previousDocumentOverflow;
  };

  const close = (persistDays = dismissalDays) => {
    if (!isOpen) return;
    isOpen = false;
    window.clearTimeout(landingTimer);
    window.clearTimeout(closeTimer);
    if (persistDays > 0) suppressFor(persistDays);
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    restoreScroll();
    closeTimer = window.setTimeout(() => {
      root.classList.remove('is-mounted', 'is-animating', 'is-landed', 'is-success', 'has-errors', 'is-initial-focus', 'is-using-pointer');
      root.hidden = true;
      showTeaser();
      if (lastFocus && lastFocus.isConnected) lastFocus.focus({ preventScroll: true });
    }, 190);
  };

  const handleKeydown = (event) => {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close(dismissalDays);
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusable();
    if (!items.length) {
      event.preventDefault();
      shell.focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const open = () => {
    if (isOpen) return;
    hideTeaser();
    teaserOpen?.setAttribute('aria-expanded', 'true');
    syncPreviewBarInset();
    isOpen = true;
    lastFocus = document.activeElement;
    previousBodyOverflow = document.body.style.overflow;
    previousDocumentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    root.hidden = false;
    root.removeAttribute('aria-hidden');
    root.classList.add('is-mounted');

    if (!force && !success && !hasErrors) safeSet('sessionStorage', sessionKey, '1');
    if (success) {
      root.classList.add('is-success', 'is-landed');
      suppressFor(30);
      back.removeAttribute('inert');
    } else if (hasErrors) {
      root.classList.add('has-errors', 'is-landed');
      back.removeAttribute('inert');
    } else {
      back.setAttribute('inert', '');
      root.classList.add('is-animating');
      landingTimer = window.setTimeout(land, landingDelay);
    }

    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
      if (!success && !hasErrors && !reduceMotion && closeButton) {
        root.classList.add('is-initial-focus');
        closeButton.focus({ preventScroll: true });
      }
    });
    if (success || hasErrors || reduceMotion) {
      window.clearTimeout(landingTimer);
      land();
    }
  };

  root.querySelectorAll('[data-wired808-close]').forEach((button) => {
    button.addEventListener('click', () => close(dismissalDays));
  });

  veil.addEventListener('click', () => close(dismissalDays));
  root.addEventListener('keydown', (event) => {
    root.classList.remove('is-initial-focus');
    if (event.key === 'Tab') root.classList.remove('is-using-pointer');
  }, { capture: true });
  root.addEventListener('pointerdown', () => {
    root.classList.remove('is-initial-focus');
    root.classList.add('is-using-pointer');
  }, { capture: true });
  document.addEventListener('keydown', handleKeydown);
  window.addEventListener('resize', syncPreviewBarInset, { passive: true });

  if (form && submit) {
    form.addEventListener('submit', () => {
      submit.disabled = true;
      submit.textContent = 'UNLOCKING…';
    });
  }

  if (copyButton) {
    copyButton.addEventListener('click', async () => {
      const code = copyButton.dataset.code || 'WIRED808';
      try {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
          throw new Error('Clipboard API unavailable');
        }
        await navigator.clipboard.writeText(code);
      } catch (_error) {
        const fallback = document.createElement('textarea');
        fallback.value = code;
        fallback.setAttribute('readonly', '');
        fallback.style.position = 'fixed';
        fallback.style.opacity = '0';
        document.body.appendChild(fallback);
        fallback.select();
        if (typeof document.execCommand === 'function') {
          document.execCommand('copy');
        }
        fallback.remove();
      }
      copyButton.textContent = `COPIED — ${code}`;
      copyButton.setAttribute('aria-label', `${code} copied`);
      suppressFor(30);
    });
  }

  const shouldSkip = !force && !success && !hasErrors && (
    safeGet('sessionStorage', sessionKey) === '1' || isSuppressed()
  );

  const openAfterConsent = () => {
    syncPreviewBarInset();
    if (consentIsVisible()) {
      consentTimer = window.setTimeout(openAfterConsent, 250);
      return;
    }
    window.clearTimeout(consentTimer);
    open();
  };

  const scheduleOpen = () => {
    const firstOpenDelay = success || hasErrors ? 0 : Math.max(normalDelay, consentProbeDelay);
    consentTimer = window.setTimeout(openAfterConsent, firstOpenDelay);
  };

  teaserOpen?.addEventListener('click', () => {
    hideTeaser();
    openAfterConsent();
  });

  teaserClose?.addEventListener('click', (event) => {
    event.stopPropagation();
    suppressTeaserFor(dismissalDays);
    hideTeaser();
    teaserClose.blur();
  });

  if (shouldSkip) {
    showTeaser();
    return;
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleOpen, scheduleOpen);
  } else {
    scheduleOpen();
  }
})();
